import React from "react";
import { useNavigate } from "react-router-dom";
import { getPharmacyFolaPrompt } from "@/pages/pharmacy/pharmacyFolaPrompts.js";

const FOLA_ICON = "/icons/appointments/Chat with Fola/Fola.svg";
const CLOSE_ICON = "/icons/pharmacy/circle-x.svg";

function buildPromptRoute({ route = "", promptKey = "" } = {}) {
  const target = String(route || "").trim();
  const key = String(promptKey || "").trim();
  if (!target || !key) return target;
  if (target.includes("?prompt=")) return target;
  const separator = target.includes("?") ? "&" : "?";
  return `${target}${separator}prompt=${encodeURIComponent(key)}`;
}

export default function PharmacyFolaSmartCard({
  title,
  body,
  cta,
  route,
  promptKey = "",
  actions = null,
  returnTo = "",
  returnToState = null,
  className = "",
  dismissible = true,
  actionVariant = "primary",
  requirement = false,
}) {
  const navigate = useNavigate();
  const [entered, setEntered] = React.useState(false);
  const [thinking, setThinking] = React.useState(false);
  const [messageEntered, setMessageEntered] = React.useState(false);
  const [typedTitle, setTypedTitle] = React.useState("");
  const [typedBody, setTypedBody] = React.useState("");
  const [typingComplete, setTypingComplete] = React.useState(false);
  const [ctaEntered, setCtaEntered] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    setEntered(false);
    setThinking(false);
    setMessageEntered(false);
    setTypedTitle("");
    setTypedBody("");
    setTypingComplete(false);
    setCtaEntered(false);

    const entryTimer = window.setTimeout(() => setEntered(true), 120);
    const thinkingTimer = window.setTimeout(() => setThinking(true), 220);
    const messageTimer = window.setTimeout(() => {
      setThinking(false);
      setMessageEntered(true);
    }, 720);

    return () => {
      window.clearTimeout(entryTimer);
      window.clearTimeout(thinkingTimer);
      window.clearTimeout(messageTimer);
    };
  }, [body, title]);

  React.useEffect(() => {
    if (!messageEntered) return undefined;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setTypedTitle(String(title || ""));
      setTypedBody(String(body || ""));
      setTypingComplete(true);
      setCtaEntered(true);
      return undefined;
    }

    const fullText = body ? `${title}\n${body}` : String(title || "");
    let index = 0;
    const charsPerTick = fullText.length > 140 ? 2 : 1;
    const interval = window.setInterval(() => {
      index = Math.min(fullText.length, index + charsPerTick);
      const nextText = fullText.slice(0, index);
      const [nextTitle = "", ...bodyParts] = nextText.split("\n");
      setTypedTitle(nextTitle);
      setTypedBody(bodyParts.join("\n"));
      if (index >= fullText.length) {
        window.clearInterval(interval);
        setTypingComplete(true);
        window.setTimeout(() => setCtaEntered(true), 140);
      }
    }, 16);

    return () => window.clearInterval(interval);
  }, [body, messageEntered, title]);

  function navigateToAction(action = {}) {
    if (typeof action.onClick === "function") {
      action.onClick();
      return;
    }
    const target = buildPromptRoute({
      route: action.route || route,
      promptKey: action.promptKey || promptKey,
    });
    if (!target) return;

    const key = action.promptKey || promptKey;
    const initialDraft = getPharmacyFolaPrompt(key);
    if (initialDraft && target.startsWith("/pharmacy/messages")) {
      navigate(target, {
        state: {
          conversationTitle: "Fola",
          conversationType: "fola",
          returnTo,
          returnToState,
          initialDraft,
          promptKey: key,
        },
      });
      return;
    }

    navigate(target);
  }

  // Institution surfaces use the same truth gate as patient and clinician
  // surfaces. Existing callers without an authoritative requirement stay
  // quiet; a future card must opt in with a persisted, unfinished condition.
  if (requirement !== true || hidden) return null;

  const ctaActions = Array.isArray(actions) && actions.length
    ? actions
    : cta
      ? [{ label: cta, route, promptKey }]
      : [];
  const actionClass =
    actionVariant === "tonal"
      ? "inline-flex min-h-10 items-center justify-center rounded-xl bg-[var(--state-primary-12)] px-3 text-center text-label-large text-[rgb(var(--afrimed-primary))] transition-colors hover:bg-[var(--state-primary-16)]"
      : "inline-flex min-h-10 items-center rounded-xl bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))] transition-opacity hover:opacity-95";
  const actionLayoutClass =
    actionVariant === "tonal" && ctaActions.length >= 6
      ? "grid grid-cols-3 gap-2"
      : "flex flex-wrap gap-2";

  return (
    <section
      className={[
        "relative scroll-mt-24 scroll-mb-28 overflow-visible rounded-[12px] bg-[rgba(var(--afrimed-primary),0.045)] px-4 py-3 text-on-surface",
        "shadow-[0_1px_0px_rgba(24,50,119,0.012)] ring-1 ring-[rgba(var(--afrimed-primary),0.035)]",
        "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
        entered ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <style>
        {`
          @keyframes afrimed-fola-dot {
            0%, 80%, 100% { opacity: .34; transform: translateY(0); }
            40% { opacity: 1; transform: translateY(-2px); }
          }
        `}
      </style>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={FOLA_ICON}
              alt=""
              className={[
                "h-5 w-5 shrink-0 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                entered ? "scale-100 opacity-100" : "scale-95 opacity-0",
              ].join(" ")}
              draggable="false"
            />
            <p className="text-label-large text-on-surface-var">Fola</p>
          </div>
          <div className="mt-1 min-h-[58px]">
            <div
              className={[
                "flex h-6 items-center gap-1 transition-opacity duration-150 motion-reduce:transition-none",
                thinking && !messageEntered ? "opacity-100" : "pointer-events-none opacity-0",
              ].join(" ")}
              aria-hidden={!thinking || messageEntered}
            >
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--afrimed-primary))]"
                  style={{
                    animation: "afrimed-fola-dot 780ms ease-in-out infinite",
                    animationDelay: `${dot * 120}ms`,
                  }}
                />
              ))}
            </div>
            <div
              className={[
                "transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                messageEntered ? "-mt-6 translate-y-0 opacity-100" : "-mt-6 translate-y-1 opacity-0",
              ].join(" ")}
            >
              <p className="text-label-large text-on-surface">
                {typedTitle}
                {messageEntered && !typingComplete ? (
                  <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-0.5 bg-[rgb(var(--afrimed-primary))]" aria-hidden="true" />
                ) : null}
              </p>
              {body && (typedBody || typingComplete) ? (
                <p className="mt-1 max-w-[980px] whitespace-pre-line text-body-small text-on-surface-var">
                  {typedBody}
                </p>
              ) : null}
            </div>
          </div>
          {ctaActions.length ? (
            <div
              className={[
              "mt-1.5 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none",
              actionLayoutClass,
              ctaEntered ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
            ].join(" ")}
          >
              {ctaActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={actionClass}
                  onClick={() => navigateToAction(action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full opacity-85 transition-opacity hover:opacity-100"
            aria-label="Dismiss"
            onClick={() => setHidden(true)}
          >
            <img src={CLOSE_ICON} alt="" className="h-5 w-5 opacity-80" draggable="false" />
          </button>
        ) : null}
      </div>
    </section>
  );
}
